import * as Yup from 'yup';
import { getRequest, postRequest, putRequest } from '../../../../services/httpService';

// 1. INTERFACES
export interface AdditionalInfo {
  marital_status: string | null;
  employee_number: string | null;
  pf_number: string | null;
  pan_number: string | null;
  access_card_number: string | null;
  experience: string | null;
  education: string | null;
  is_discharge_approve: boolean ;
  registration_number: string | null;
  bank_name: string | null;
  address: string | null;
  branch_name: string | null;
  account_number: string | null;
  ifsc: string | null;
  micr_number: string | null;
  address_same_as_permenant: boolean ;
  permenant_address_line1?: string | null;
  permenant_address_line2?: string | null;
  permenant_city?: string | null;
  permenant_state?: string | null;
  permenant_country?: string | null;
  permenant_pincode ?: string | null;
  communication_address_line1?: string | null;
  communication_address_line2?: string | null;
  communication_city?: string | null;
  communication_state?: string | null;
  communication_country?: string | null;
  communication_pincode ?: string | null;
}

export interface ClinicDepartment {
  clinic: number;
  clinic_name: string;
  department: { id: number; name?: string }[];
  is_default: boolean;
}

export interface FileUpload {
  file: string;
}

export interface UserInitialValues {
  email: string;
  username: string;
  password: string;
  confirm_password: string;
  first_name: string;
  last_name: string;
  gender: number;
  dob: string;
  doj: string;
  designation: string;
  role: number |null;
  photo: string;
  is_active: boolean;
  user_type: string;
  specialization: string;
  doctor_type: string;
  doctor_category: string;
  mobile_number: string;
  marketing_executive: string;
  additional_info: AdditionalInfo;
  files: FileUpload[];
  clinic_departments: ClinicDepartment[];
}

export interface Gender {
  id: number;
  created_on: string;
  updated_on: string;
  deleted_on: string | null;
  deleted_reason: string | null;
  name: string;
  is_active: boolean;
  tenant: number;
}

export interface Meta {
  limit: number;
  next: string | null;
  offset: number;
  previous: string | null;
  total_count: number;
}

export interface GenderListResponse {
  meta: Meta;
  objects: Gender[];
}

export interface Clinic {
  id: number;
  created_on: string;
  updated_on: string;
  deleted_on: string | null;
  deleted_reason: string | null;
  name: string;
  code: string;
  description: string;
  phone: string;
  website: string;
  email: string | null;
  area: string | null;
  city: string | null;
  country: string | null;
  landmark: string | null;
  line_1: string | null;
  line_2: string | null;
  pincode: string | null;
  state: string | null;
  tin_no: string | null;
  gst_no: string | null;
  fax_no: string | null;
  licence_no: string | null;
  regitration_no: string | null;
  establishment_no: string | null;
  pan_no: string | null;
  cluster: string | null;
  server: string | null;
  database: string | null;
  tenant: number;
}

export interface DropdownType {
  id: number;
  type: string;
  name: string;
  code: string;
  related_name: string;
  is_active: boolean;
}
export interface Role {
  id: number;
  created_on: string;
  updated_on: string;
  deleted_on: string | null;
  deleted_reason: string | null;
  name: string;
  is_doctor: boolean;
  tenant: number;
}
export interface Department {
  id: number;
  name: string;
  clinic__id: number;
  clinic__name: string;
  tenant__name: string;
}

export interface UserList {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  username: string;
  role: string;
  departments: Department[];
  appointment_type: string;
  is_active: boolean;
  user_type: string | null;
  is_locked: boolean;
}
export interface PaginatedUserList {
  meta: Meta;
  objects: UserList[];
}
export interface PaginatedClinicList {
  meta: Meta;
  objects: Clinic[];
}
export interface PaginatedRole {
  meta: Meta;
  objects: Role[];
}
export interface PaginatedDepartmentList {
  meta: Meta;
  objects: Department[];
}

export interface configurations {
  DESIGNATION: DropdownType[];
  SPECIALIZATION: DropdownType[];
  DOCTOR_TYPE: DropdownType[];
  DOCTOR_CATEGORY: DropdownType[];
  MARITAL_STATUS: DropdownType[];
}

// 2. INITIAL VALUES
export const userInitialValues: UserInitialValues = {
  email: '',
  username: '',
  password: '',
  confirm_password: '',
  first_name: '',
  last_name: '',
  gender: 0,
  dob: '',
  doj: '',
  user_type: 'EMPLOYEE',
  // Employee only
  role: null,
  designation: '',
  // Doctor only
  specialization: '',
  doctor_type: '',
  doctor_category: '',
  marketing_executive: '',
  // Common/other
  photo: '',
  is_active: true,
  mobile_number: '',
  additional_info: {
    marital_status: null,
    employee_number: null,
    pf_number: null,
    pan_number: null,
    access_card_number: null,
    experience: null,
    education: null,
    is_discharge_approve: false,
    registration_number: null,
    bank_name: null,
    address: null,
    branch_name: null,
    account_number: null,
    ifsc: null,
    micr_number: null,
    address_same_as_permenant: false,
    permenant_address_line1: null,
    permenant_address_line2: null,
    permenant_city: null,
    permenant_state: null,
    permenant_country: null,
    permenant_pincode : null,
    communication_address_line1: null,
    communication_address_line2: null,
    communication_city: null,
    communication_state: null,
    communication_country: null,
    communication_pincode : null
  },
  files: [],
  clinic_departments: []
};

// 3. YUP VALIDATION
export const userValidationSchema = Yup.object().shape({
  first_name: Yup.string().required('First name is required'),
  last_name: Yup.string().required('Last name is required'),
  gender: Yup.number().required('Gender is required'),
  username: Yup.string().required('Username is required'),
  mobile_number: Yup.string().required('Mobile number is required'),
  email: Yup.string().email('Invalid email').required('Email is required'),
  password: Yup.string()
    .required('Password is required')
    .min(8, 'Password must be at least 8 characters')
    .matches(/[A-Z]/, 'Password must contain at least one uppercase letter')
    .matches(/[a-z]/, 'Password must contain at least one lowercase letter')
    .matches(/[0-9]/, 'Password must contain at least one number')
    .matches(/[@$!%*?&#]/, 'Password must contain at least one special character'),
  confirm_password: Yup.string()
    .required('Confirm Password is required')
    .oneOf([Yup.ref('password')], 'Passwords must match'),
  user_type: Yup.string(),
  designation: Yup.string().when('user_type', (user_type, schema) => {
    const type = Array.isArray(user_type) ? user_type[0] : user_type;
    return type === 'EMPLOYEE' ? schema.required('Designation is required') : schema;
  }),
  role: Yup.number().when('user_type', (user_type, schema) => {
    const type = Array.isArray(user_type) ? user_type[0] : user_type;
    return type === 'EMPLOYEE' ? schema.required('Role is required') : schema;
  }),
  specialization: Yup.string().when('user_type', (user_type, schema) => {
    const type = Array.isArray(user_type) ? user_type[0] : user_type;
    return type === 'DOCTOR' ? schema.required('Specialization is required') : schema;
  }),
  doctor_type: Yup.string().when('user_type', (user_type, schema) => {
    const type = Array.isArray(user_type) ? user_type[0] : user_type;
    return type === 'DOCTOR' ? schema.required('Doctor type is required') : schema;
  }),
  // Optional fields
  dob: Yup.string(),
  doj: Yup.string(),
  doctor_category: Yup.string(),
  marketing_executive: Yup.string(),
  photo: Yup.string(),
  is_active: Yup.boolean(),
  additional_info: Yup.object().shape({
    marital_status: Yup.string().nullable(),
    employee_number: Yup.string().nullable(),
    pf_number: Yup.string().nullable(),
    pan_number: Yup.string().required('PAN no is required'),
    access_card_number: Yup.string().nullable(),
    experience: Yup.string().nullable(),
    education: Yup.string().nullable(),
    is_discharge_approve: Yup.boolean().nullable(),
    registration_number: Yup.string().nullable(),
    bank_name: Yup.string().nullable(),
    address: Yup.string().nullable(),
    branch_name: Yup.string().nullable(),
    account_number: Yup.string().nullable(),
    ifsc: Yup.string().nullable(),
    micr_number: Yup.string().nullable(),
    permenant_address_line1: Yup.string().nullable(),
    permenant_address_line2: Yup.string().nullable(),
    permenant_city: Yup.string().nullable(),
    permenant_state: Yup.string().nullable(),   
    permenant_country: Yup.string().nullable(),
    permenant_pincode : Yup.string().nullable(),
    communication_address_line1: Yup.string().nullable(),
    communication_address_line2: Yup.string().nullable(),
    communication_city: Yup.string().nullable(),
    communication_state: Yup.string().nullable(),
    communication_country: Yup.string().nullable(),
    communication_pincode : Yup.string().nullable(),
    address_same_as_permenant: Yup.boolean().nullable(),
  }),
  files: Yup.array().of(
    Yup.object().shape({
      file: Yup.string().required()
    })
  ),
  clinic_departments: Yup.array()
    .of(
      Yup.object().shape({
        clinic: Yup.number().required('Please select a clinic.'),
        clinic_name: Yup.string().required('Clinic name is required.'),
        department: Yup.array()
          .of(
            Yup.object().shape({
              id: Yup.number().required('Please select a department.'),
              name: Yup.string().required('Department name is required.')
            })
          )
          .min(1, 'Please select at least one department.')
          .required('Please select at least one department.'),
        is_default: Yup.boolean().required()
      })
    )
    .min(1, 'Please add at least one clinic and its department.')
    .required('Please add at least one clinic and its department.'),
});

// 4. API CALL FUNCTIONS
export function getGender(): Promise<GenderListResponse> {
  const url = '/genders/';  
  return getRequest(url);
}  

export function getClinicList(): Promise<PaginatedClinicList> {
  const url = '/clinics/';  
  return getRequest(url);
}  

export function getDepartmentbyclinic(id:number): Promise<PaginatedDepartmentList> {
  const url = `departments/?clinic__id=${id}`;  
  return getRequest(url);
}

export function getConfigurations(): Promise<configurations> {
  const url = '/clinics/configuration/?module_type=user';  
  return getRequest(url);
}
export function getRoleList(): Promise<PaginatedRole> {
  const url = '/roles/?is_doctor=false';  
  return getRequest(url);
}

export function postUserDetails(data: UserInitialValues): Promise<UserInitialValues> {
  const url = '/users/';
  return postRequest(url, data);
}

export function getUserDetails(id:number): Promise<UserInitialValues> {
  const url = `/users/${id}/`;  
  return getRequest(url);
}

export function getUserList(pageSize:number, offset:number, searchTerm?:string, ): Promise<PaginatedUserList> {
  const url = `/users/?limit=${pageSize}&offset=${offset}&search=${searchTerm || ''}`;  
  return getRequest(url);
}

export function putLockUnlockUser(id:number, data:{action:string}): Promise<null> {
  const url = `users/${id}/unlock-user/`;
  return putRequest(url, data);
}


// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function putUserDetails(id:number, data: any): Promise<any> {
  const url = `/users/${id}/`;
  return putRequest(url, data);
}
export function checkUserAvailability(query:string): Promise<{available: boolean,message: string}> {
  const url = `users/check-username/?username=${query}`;  
  return getRequest(url);
}
export function changeUserStatus(id:number, data:{is_active:boolean}): Promise<null> {
  const url = `users/${id}/set-active/`;
  return putRequest(url, data);
}
